import { useState, useContext, useEffect } from 'react';
import { Paper, Box, Grid, Typography } from '@mui/material';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import useReportsService from '../../services/ReportsService';
import { AppContext, SttFullDialog } from '../../sporttia/all';
import { PAGE_REPORT, PAGE_REPORT_ROWS } from '../../lib/queryKeys';
import SttCachedTable from '../../sporttia/SttCachedTable';
import { SttTopFilteringControls } from '../../sporttia/SttTopFilteringControls';
import { useInteractionsFiles } from '../../lib/hooks';
import { getPath } from '../Pages';
import constants from '../../config/constants';
import { ColumnPopup } from '../../sporttia/dialogs/ColumnPopup';
import { canSeeActivityReports } from './reports/canSeeActivityReports';

/**
 * Converts the columns returned by the API to columns that the SttTopFilteringControls component can understand
 * @param columns Columns returned by the api.
 */
const getColumns = (columns: any[], align = 'C') => {
    if (!Array.isArray(columns)) {
        return []; // Devuelvo un array vacio para evitar errores
    }
    let columnsPositions: any = [];
    columns.forEach((column: any) => {
        if (column?.field) {
            columnsPositions[column?.field] = column?.position;
        }
    });
    // Extraemos las entradas del array con el campo de la columna y su posicion,
    // ordenamos en base al valor numerico (posición, elemento 1 del vector)
    // por último reducimos el array resultante ordenado de componentes {field, position}
    // y vamos creando un objeto con todos los fields ordenados
    columnsPositions = Object.entries(columnsPositions)
        .sort(
            (columnLeft: any, columnRight: any) =>
                columnLeft[1] - columnRight[1],
        )
        .reduce((previousValue: any, currentValue: any) => {
            previousValue[currentValue[0]] = null;
            return previousValue;
        }, {});
    const formattedColumns: any = [];
    columns.forEach((col: any) => {
        // Class
        let className;
        switch (align) {
            case 'L':
                className = 'text-left';
                break;
            case 'R':
                className = 'text-right';
                break;
            default:
                className = 'text-center';
        }

        // Type
        const type = '';

        formattedColumns[col?.field || '*'] = {
            field: col.field,
            title: col.caption,
            width: col.width,
            className,
            type,
        };
    });
    // Asignamos las columnas formateadas siguiendo el orden definido en columnsPositions
    // para las columnas
    return Object.values(Object.assign(columnsPositions, formattedColumns));
};

/**
 * Translates filter caption that may contain [[key]] placeholders.
 * Example: "[[Id]] [[Activity]]" becomes "Id Actividad" in Spanish.
 * @param caption - The caption string that may contain [[key]] placeholders
 * @param t - The translation function from useTranslation
 * @returns Translated caption string
 */
const translateFilterCaption = (
    caption: string,
    t: (key: string, options?: { defaultValue: string }) => string,
): string => {
    if (!caption) return '';

    // Check if caption contains [[key]] placeholders
    const placeholderPattern = /\[\[([^\]]+)\]\]/g;
    const hasPlaceholders = placeholderPattern.test(caption);

    if (!hasPlaceholders) {
        // No placeholders - try to translate directly
        return t(`reports.filter.${caption}`, { defaultValue: caption });
    }

    // Reset regex after test
    placeholderPattern.lastIndex = 0;

    // Replace each [[key]] placeholder with translated value
    return caption.replace(placeholderPattern, (_match, key) => {
        return t(`reports.filter.${key}`, { defaultValue: key });
    });
};

// Unused - kept for reference
// const SUBSCRIPTION_FIELDS = [
//     'inscriptionIni',
//     'inscriptionEnd',
//     'unsubscriptionIni',
//     'unsubscriptionEnd',
// ];

// Unused function - kept for reference
// const _getFilters = (filters: any[], cxt: any) => {
//     if (!Array.isArray(filters) || !cxt?.t) {
//         return [];
//     }
//     const formatFilter = ({ field, type, value, name }: any) => ({
//         name: field,
//         caption: name,
//         type,
//         value:
//             value && type?.toLowerCase() === 'date'
//                 ? moment(value, 'DD-MM-YYYY').format('YYYY-MM-DD')
//                 : value || '',
//     });
//     const fieldNames = filters.map((f: any) => f.field);
//     const hasSubscriptionFilters =
//         fieldNames.some((f: any) => f?.startsWith('inscription')) &&
//         fieldNames.some((f: any) => f?.startsWith('unsubscription'));
//     if (!hasSubscriptionFilters) {
//         return filters.map(formatFilter);
//     }
//     const regularFilters = filters
//         .filter((ft: any) => !SUBSCRIPTION_FIELDS.includes(ft.field))
//         .map(formatFilter);
//     return [
//         ...regularFilters,
//         {
//             type: 'selectPeriod',
//             sm: 5,
//             selectCaption: cxt.t('groupMemberSubscriptionFilter'),
//             selectName: 'subscriptionType',
//             selectValue: 'all',
//             selectOptions: [
//                 {
//                     caption: cxt.t('groupMemberSubscription'),
//                     value: 'inscription',
//                 },
//                 {
//                     caption: cxt.t('groupMemberCancelation'),
//                     value: 'unsubscription',
//                 },
//                 {
//                     caption: cxt.t('All'),
//                     value: 'all',
//                 },
//             ],
//             nameIni: 'subscriptionIni',
//             valIni: moment().startOf('month').format('YYYY-MM-DD'),
//             nameEnd: 'subscriptionEnd',
//             valEnd: moment().format('YYYY-MM-DD'),
//             disableOnValue: 'all',
//         },
//     ];
// };

type Props = {
    id?: number | null;
    navigateBack?: string | null;
};

/**
 * Report view.
 * @param id Report ID.
 * @returns {React.ReactElement}
 */
export default function Report({
    id = null,
    navigateBack = null,
}: Props): React.ReactElement | null {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const { downloadFile } = useInteractionsFiles();
    const params = useParams<{ id: string }>();
    const reportsService = useReportsService();
    const [reportId, setReportId] = useState<number | null>(id);
    const [columns, setColumns] = useState<any>();
    const [filtersTopControls, _setFiltersTopControls] = useState<any>();
    const [filters, setFilters] = useState<any>();
    const [summatory, setSummatory] = useState<any>();
    const [reportName, setReportName] = useState<string>('');
    const [pageAndRows, setPageAndRows] = useState<any>();
    const [isColumnPopupOpen, setColumnPopupOpen] = useState<boolean>(false);
    const [_selectedAlign, setSelectedAlign] = useState<string>('C');
    const navigate = useNavigate();
    const location = useLocation();
    const reportQuery = useQuery({
        queryKey: [PAGE_REPORT, { id: reportId }],
        queryFn: () => reportsService.get(reportId as number),
        enabled: !!reportId,
    } as any);

    let reportGroups: any = location.state?.reportGroups ?? null;
    if (!reportGroups) {
        reportGroups = [
            {
                caption: 'OccupationOfFacilities',
                path: 'report',
                params: { id: 8 },
            },
            {
                caption: 'Seating',
                path: 'report',
                params: { id: 53 },
            },
            {
                caption: 'tpvRecordsBySc',
                path: 'report',
                params: { id: 27 },
            },
            {
                caption: 'AdminSports',
                path: 'report',
                params: { id: 43 },
            },
            {
                caption: 'increasesInEnrollmentInActivitiesPerYear',
                path: 'report',
                params: { id: 33 },
            },
            {
                caption: 'increasesInEnrollmentInActivitiesPerYear',
                path: 'report',
                params: { id: 32 },
            },
            {
                caption: 'playtomicCenters',
                path: 'report',
                params: { id: 44 },
            },
            {
                caption: 'playtomicCenters',
                path: 'report',
                params: { id: 39 },
            },
            {
                caption: 'events',
                path: 'report',
                params: { id: 14 },
            },
            {
                caption: 'rentals',
                path: 'report',
                params: { id: 28 },
            },
            {
                caption: 'rentalsPerDay',
                path: 'report',
                params: { id: 37 },
            },
            {
                caption: 'rentalsRatioByCenter',
                path: 'report',
                params: { id: 30 },
            },
            {
                caption: 'rentalsRatioPerYear',
                path: 'report',
                params: { id: 9 },
            },
            {
                caption: 'emails',
                path: 'report',
                params: { id: 4 },
            },
            {
                caption: 'notificationsTypes',
                path: 'report',
                params: { id: 17 },
            },
            {
                caption: 'pushNotifications',
                path: 'report',
                params: { id: 31 },
            },
            {
                caption: 'smss',
                path: 'report',
                params: { id: 16 },
            },
        ];
    }

    if (
        canSeeActivityReports(cxt) ||
        cxt?.user?.role === constants.roles.admin
    ) {
        reportGroups = reportGroups.concat([
            {
                caption: 'sc.reportClass.studentList',
                path: 'report',
                params: { id: 11 },
            },
            {
                caption: 'activitiesStudentsTotals',
                path: 'report',
                params: { id: 1 },
            },
            {
                caption: 'ReportActivitiesByMonth',
                path: 'report',
                params: { id: 19 },
            },
            {
                caption: 'ReportMissedClasses',
                path: 'report',
                params: { id: 22 },
            },
            {
                caption: 'ReportStudentFees',
                path: 'report',
                params: { id: 20 },
            },
        ]);
    }
    /**
     * Método para descargar los ficheros de los informes.
     * TODO migrar a react-query
     * @param format [pdf|csv] string format.
     */
    function download(format: string) {
        const type = format === 'csv' ? 'text/csv' : 'application/pdf';

        // PDF
        if (format === 'pdf') {
            downloadFile(
                `${constants.boundedContextPrefixes.MANAGER}/reports/${reportId}.pdf?page=${pageAndRows.page}&rows=${pageAndRows.rows}&filter=${filters.filter}`,
                type,
                {},
                `informe.${format}`,
            );
        }

        // CSV
        if (format === 'csv') {
            downloadFile(
                `${constants.boundedContextPrefixes.MANAGER}/reports/${reportId}.csv?page=${pageAndRows.page}&rows=${pageAndRows.rows}&filter=${filters.filter}`,
                type,
                {},
                `informe.${format}`,
            );
        }
    }

    /**
     * Formats filter object to API query string format: key1::value1,key2::value2
     * Transforms subscriptionType + dates into the correct API filter names
     */
    const formatFilterParam = (filterObj: any) => {
        const { subscriptionType, subscriptionIni, subscriptionEnd, ...rest } =
            filterObj;

        // Build final filters object
        const finalFilters = { ...rest };

        // Map subscriptionType to correct API filter names
        if (subscriptionType === 'inscription') {
            finalFilters.inscriptionIni = subscriptionIni;
            finalFilters.inscriptionEnd = subscriptionEnd;
        } else if (subscriptionType === 'unsubscription') {
            finalFilters.unsubscriptionIni = subscriptionIni;
            finalFilters.unsubscriptionEnd = subscriptionEnd;
        }
        // If 'all', no date filters are added

        // Convert to query string format, excluding empty values
        return Object.entries(finalFilters)
            .filter(
                ([, value]) =>
                    value !== undefined && value !== null && value !== '',
            )
            .map(([key, value]) => `${key}::${value}`)
            .join(',');
    };

    /**
     * When "id" or "params" change we set the "reportId" variable.
     */
    useEffect(() => {
        if (id !== null) setReportId(id);
        else if (params?.id) setReportId(Number(params.id));
    }, [id, params]);

    /**
     * When reportQuery data is available, extract columns and filters.
     */
    useEffect(() => {
        if (!reportQuery.data) return;

        const response = reportQuery.data as {
            report?: {
                columns?: any[];
                filters?: any[];
                name?: string;
            };
        };

        const report = response?.report;

        // Set columns from report data
        if (report?.columns && report.columns.length > 0) {
            setColumns(getColumns(report.columns));
        }

        // Set filters from report data
        if (report?.filters && report.filters.length > 0) {
            _setFiltersTopControls(
                report.filters.map((filter: any) => ({
                    name: filter.field,
                    caption: translateFilterCaption(filter.caption, t),
                    type: filter.type?.toLowerCase(),
                    value: filter.value || '',
                })),
            );
        }

        // Set report name for title
        if (report?.name) {
            setReportName(report.name);
        }
    }, [reportQuery.data, t]);

    const handleOpenColumnPopup = () => {
        setColumnPopupOpen(true);
    };

    const handleCloseColumnPopup = () => {
        setColumnPopupOpen(false);
    };

    const handleSaveColumnAlign = (align: string) => {
        setSelectedAlign(align);
        setColumns((prevColumns: any) => getColumns(prevColumns, align));
        handleCloseColumnPopup();
    };

    if (!reportId) {
        return null;
    }

    return (
        <>
            <SttFullDialog
                open={!!(id || reportId)}
                onClose={() => {
                    if (navigateBack) {
                        navigate(getPath(navigateBack as any));
                    } else {
                        navigate(getPath('scReports' as any));
                    }
                }}
                title={
                    id || reportId
                        ? t(`reports.report_${id || reportId}`, {
                              defaultValue: reportName || t('Report'),
                          })
                        : t('Report')
                }
            >
                <Paper>
                    <Box>
                        <SttTopFilteringControls
                            p={2}
                            extraFields={filtersTopControls}
                            menu={
                                pageAndRows?.rows > 0
                                    ? [
                                          {
                                              caption: `${t('Download')} PDF`,
                                              onClick: () => download('pdf'),
                                          },
                                          {
                                              caption: `${t('Download')} CSV`,
                                              onClick: () => download('csv'),
                                          },
                                          {
                                              caption: t(
                                                  'ConfigureColumnAlignment',
                                              ),
                                              onClick: handleOpenColumnPopup,
                                          },
                                      ]
                                    : undefined
                            }
                            onFilter={(filter: any) =>
                                setFilters({
                                    filter: formatFilterParam(filter),
                                })
                            }
                        />
                        <SttCachedTable
                            queryKey={PAGE_REPORT_ROWS}
                            queryFn={async (queryParams: any) => {
                                const reportRows =
                                    (await reportsService.getReportsRows(
                                        reportId as number,
                                        queryParams,
                                    )) as {
                                        rows?: unknown[];
                                        count?: number;
                                        summatory?: unknown;
                                    };
                                if (reportRows?.rows && reportRows?.count) {
                                    setPageAndRows({
                                        page: 1,
                                        rows:
                                            reportRows.count *
                                            reportRows.rows.length,
                                    });
                                }
                                return reportRows;
                            }}
                            queryParams={filters ?? null}
                            queryOptions={{
                                enabled:
                                    !!reportId &&
                                    reportQuery.status === 'success',
                                onSuccess: (data: any) =>
                                    setSummatory(data?.summatory),
                            }}
                            prefetching={false}
                            columns={columns}
                        />
                    </Box>
                </Paper>
                <Box mt={2}>
                    {summatory && (
                        <Grid container spacing={2} justifyContent="flex-end">
                            <Grid>
                                <Paper>
                                    <Box p={2}>
                                        <Typography
                                            align="center"
                                            variant="overline"
                                            display="block"
                                        >
                                            {t('Price')}
                                        </Typography>
                                        <Typography align="center" variant="h5">
                                            {summatory.price
                                                ? summatory.price
                                                : 0.0}{' '}
                                            {t('Coin')}
                                        </Typography>
                                    </Box>
                                </Paper>
                            </Grid>
                        </Grid>
                    )}
                </Box>
            </SttFullDialog>
            <ColumnPopup
                open={isColumnPopupOpen}
                onClose={handleCloseColumnPopup}
                onSave={handleSaveColumnAlign}
            />
        </>
    );
}
